/**
 * Tela /campaigns — lista de campanhas (TH-escopo-futuro).
 *
 * Referência visual: design-system/prototype/painel-screens.jsx — PainelCampanhas.
 *
 * A API ainda não lista campanhas (só cria: POST /campaigns/from-template).
 * Até existir GET /campaigns, a página mostra que a listagem não está
 * disponível — antes exibia campanhas de demonstração ao cliente (PSHWO-67349).
 * A criação a partir de template e a galeria continuam.
 */

import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Plus, LayoutGrid } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { NewCampaignDialog } from "@/components/NewCampaignDialog";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { FilterPills } from "@/components/ui/FilterPills";
import { StatusPill, type StatusTone } from "@/components/ui/StatusPill";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/Table";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CampaignStatus = "active" | "scheduled" | "draft" | "paused";

interface Campaign {
  id: string;
  name: string;
  status: CampaignStatus;
  telas: string;
  skus: number;
  when: string;
  layout: string;
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_META: Record<
  CampaignStatus,
  { tone: StatusTone; label: string }
> = {
  active: { tone: "success", label: "Ativa" },
  scheduled: { tone: "warn", label: "Agendada" },
  draft: { tone: "muted", label: "Rascunho" },
  paused: { tone: "muted", label: "Pausada" },
};

type FilterValue = "all" | CampaignStatus;

const FILTER_OPTIONS = [
  { value: "all" as const, label: "Todas", count: 12 },
  { value: "active" as const, label: "Ativas", count: 2 },
  { value: "scheduled" as const, label: "Agendadas", count: 2 },
  { value: "draft" as const, label: "Rascunho", count: 7 },
  { value: "paused" as const, label: "Pausadas", count: 1 },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CampaignsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterValue>("all");
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  // Sem endpoint de listagem ainda: nenhuma linha (ver cabeçalho).
  const campaigns: Campaign[] = [];
  const rows =
    filter === "all" ? campaigns : campaigns.filter((c) => c.status === filter);

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / CAMPANHAS"
        title="Campanhas"
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/campaigns/templates"
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[var(--color-border-strong)] bg-transparent px-4 text-sm font-medium transition-colors hover:bg-[var(--color-secondary)]"
            >
              <LayoutGrid className="h-4 w-4" aria-hidden="true" />
              Ver templates
            </Link>
            <Button onClick={() => setNewDialogOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nova campanha
            </Button>
          </div>
        }
      >
        <NewCampaignDialog
          open={newDialogOpen}
          onOpenChange={setNewDialogOpen}
          onCreateBlank={() =>
            void navigate({
              to: "/campaigns/$campaignId",
              params: { campaignId: "new" },
            })
          }
          onCloned={(campaignId) =>
            void navigate({
              to: "/campaigns/$campaignId",
              params: { campaignId },
            })
          }
        />
        <div className="mx-auto max-w-6xl">
          {campaigns.length === 0 ? (
            <p className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-6 py-10 text-center text-sm text-[var(--color-muted-foreground)]">
              A listagem de campanhas ainda não está disponível. Para criar uma
              campanha, use &quot;Ver templates&quot; ou &quot;Nova campanha&quot;.
            </p>
          ) : (
          <>
          <FilterPills
            options={FILTER_OPTIONS}
            value={filter}
            onChange={setFilter}
            className="mb-4"
          />

          <Table>
            <Thead>
              <tr>
                <Th>Campanha</Th>
                <Th>Status</Th>
                <Th>Telas</Th>
                <Th>SKUs</Th>
                <Th>Janela</Th>
                <Th>Layout</Th>
                <Th />
              </tr>
            </Thead>
            <Tbody>
              {rows.map((c) => {
                const meta = STATUS_META[c.status];
                return (
                  <Tr
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() =>
                      void navigate({
                        to: "/campaigns/$campaignId",
                        params: { campaignId: c.id },
                      })
                    }
                  >
                    <Td className="font-medium">{c.name}</Td>
                    <Td>
                      <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                    </Td>
                    <Td className="font-mono text-xs tabular-nums">
                      {c.telas}
                    </Td>
                    <Td className="font-mono text-xs tabular-nums">{c.skus}</Td>
                    <Td className="text-xs text-[var(--color-muted-foreground)]">
                      {c.when}
                    </Td>
                    <Td className="text-xs text-[var(--color-muted-foreground)]">
                      {c.layout}
                    </Td>
                    <Td className="text-right text-[var(--color-muted-foreground)]">
                      ···
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          </>
          )}
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
